import React, { useState, useEffect, useRef } from 'react';
import ingredientApi from '../../services/ingredientApi';
import { formatQuantity } from '../../utils/formatters';
import './InventoryItem.css';

/**
 * InventoryItem Component
 * Displays an individual inventory item with quantity controls and UOM selector
 * Now fully metric-based (GRAMS, MG, KG, ML, LITERS)
 *
 * @param {Object} props - Component props
 * @param {Object} props.item - Inventory item data
 * @param {Function} props.onQuantityChange - Callback when quantity changes
 * @param {Function} props.onUnitChange - Callback when the unit of measure changes
 * @param {Function} props.onNeededChange - Callback when needed/shortage values change
 * @param {boolean} props.isAdmin - Whether the user has admin privileges
 */
const InventoryItem = ({
    item,
    onQuantityChange,
    onUnitChange,
    onNeededChange,
    isAdmin
}) => {
    const [quantity, setQuantity] = useState(item.current_quantity);
    const [unitOfMeasure, setUnitOfMeasure] = useState(item.ingredient_unit);
    const [error, setError] = useState('');

    // Expandable details: supplier info (already on `item`) + which products use it (lazy-fetched)
    const [expanded, setExpanded] = useState(false);
    const [productsInfo, setProductsInfo] = useState(null);
    const [productsLoading, setProductsLoading] = useState(false);
    const [productsError, setProductsError] = useState('');

    const toggleExpanded = () => setExpanded(prev => !prev);

    // Guards the fetch with a ref rather than the productsLoading/productsInfo
    // state itself — including those in the dependency array would re-run this
    // effect the moment setProductsLoading(true) fires, and the cleanup from
    // that re-run cancels the in-flight request before it can ever resolve.
    const hasFetchedRef = useRef(false);

    useEffect(() => {
        if (!expanded || hasFetchedRef.current) return;
        hasFetchedRef.current = true;

        let cancelled = false;
        setProductsLoading(true);
        setProductsError('');

        ingredientApi.getProducts(item.ingredient_id)
            .then(data => {
                if (!cancelled) setProductsInfo(data);
            })
            .catch(() => {
                if (!cancelled) setProductsError('Failed to load product usage');
            })
            .finally(() => {
                if (!cancelled) setProductsLoading(false);
            });

        return () => { cancelled = true; };
    }, [expanded, item.ingredient_id]);

    // Metric conversion constants
    const GRAMS_TO_MG = 1000;
    const MG_TO_GRAMS = 0.001;
    const KG_TO_GRAMS = 1000;
    const GRAMS_TO_KG = 0.001;
    const LITERS_TO_ML = 1000;
    const ML_TO_LITERS = 0.001;

    // Determine step size based on the currently selected unit
    const getStepSize = (unit) => {
        switch (unit) {
            case 'MG':
                return 1;
            case 'GRAMS':
                return 0.1;
            case 'KG':
                return 0.01;
            case 'ML':
                return 1;
            case 'LITERS':
                return 0.1;
            case 'S':
                return 1;
            default:
                return 1;
        }
    };

    const step = getStepSize(unitOfMeasure);

    // Update local state when item changes
    useEffect(() => {
        setQuantity(item.current_quantity);
        setUnitOfMeasure(item.ingredient_unit);
    }, [item.current_quantity, item.ingredient_unit]);

    // Format number based on unit type — delegates to the shared formatter
    // (utils/formatters.js) so this follows the same rule as every other
    // quantity display in the app, not a locally-duplicated copy of it.
    const formatNumber = (num, unit) => formatQuantity(num, unit || unitOfMeasure);

    // Convert quantity between metric units
    const convertQuantity = (value, fromUnit, toUnit) => {
        if (isNaN(value)) return value;

        if (fromUnit === toUnit) return value;

        let convertedValue;

        // Weight conversions
        if (fromUnit === 'GRAMS' && toUnit === 'MG') {
            convertedValue = value * GRAMS_TO_MG;
        } else if (fromUnit === 'MG' && toUnit === 'GRAMS') {
            convertedValue = value * MG_TO_GRAMS;
        } else if (fromUnit === 'KG' && toUnit === 'GRAMS') {
            convertedValue = value * KG_TO_GRAMS;
        } else if (fromUnit === 'GRAMS' && toUnit === 'KG') {
            convertedValue = value * GRAMS_TO_KG;
        } else if (fromUnit === 'MG' && toUnit === 'KG') {
            convertedValue = value * MG_TO_GRAMS * GRAMS_TO_KG;
        } else if (fromUnit === 'KG' && toUnit === 'MG') {
            convertedValue = value * KG_TO_GRAMS * GRAMS_TO_MG;
        }
        // Volume conversions
        else if (fromUnit === 'LITERS' && toUnit === 'ML') {
            convertedValue = value * LITERS_TO_ML;
        } else if (fromUnit === 'ML' && toUnit === 'LITERS') {
            convertedValue = value * ML_TO_LITERS;
        }
        else {
            return value;
        }

        return parseFloat(formatNumber(convertedValue, toUnit));
    };

    // Handle quantity input change
    const handleQuantityChange = (e) => {
        const value = e.target.value;
        if (value === '') {
            setQuantity('');
            return;
        }

        const numValue = parseFloat(value);
        if (isNaN(numValue)) {
            setError('Please enter a valid number');
            return;
        }

        if (numValue < 0) {
            setError('Quantity cannot be negative');
            return;
        }

        setError('');
        setQuantity(value);
    };

    // Handle quantity blur (validate and format)
    const handleQuantityBlur = () => {
        if (quantity === '') {
            setQuantity(item.current_quantity);
            return;
        }

        const numValue = parseFloat(quantity);
        if (isNaN(numValue)) {
            setQuantity(item.current_quantity);
            return;
        }

        const formattedValue = formatNumber(numValue, unitOfMeasure);
        setQuantity(formattedValue);
        onQuantityChange(parseFloat(formattedValue), unitOfMeasure);
    };

    // Handle increment/decrement
    const handleIncrement = () => {
        const currentValue = parseFloat(quantity) || 0;
        const newValue = currentValue + step;
        const formattedValue = formatNumber(newValue, unitOfMeasure);
        setQuantity(formattedValue);
        onQuantityChange(parseFloat(formattedValue), unitOfMeasure);
    };

    const handleDecrement = () => {
        const currentValue = parseFloat(quantity) || 0;
        const newValue = Math.max(0, currentValue - step);
        const formattedValue = formatNumber(newValue, unitOfMeasure);
        setQuantity(formattedValue);
        onQuantityChange(parseFloat(formattedValue), unitOfMeasure);
    };

    // Handle unit of measure dropdown change
    const handleUnitChange = (e) => {
        const newUnit = e.target.value;
        const oldUnit = unitOfMeasure;
        const currentQuantity = parseFloat(quantity);

        if (!isNaN(currentQuantity)) {
            const convertedQuantity = convertQuantity(currentQuantity, oldUnit, newUnit);
            const formattedValue = formatNumber(convertedQuantity, newUnit);
            setQuantity(formattedValue);
            setUnitOfMeasure(newUnit);
            onUnitChange(newUnit);
            onQuantityChange(parseFloat(formattedValue), newUnit);
        } else {
            setUnitOfMeasure(newUnit);
            onUnitChange(newUnit);
        }
    };

    const getStockStatus = () => {
        const qty = parseFloat(quantity) || 0;
        const min = item.min_stock_level || 0;

        if (qty === 0) {
            return { label: 'Out of Stock', color: '#ef4444', dotClass: 'red' };
        }
        if (qty > 0 && qty < min) {
            return { label: 'Critical', color: '#f97316', dotClass: 'orange' };
        }
        if (qty >= min && qty <= min * 1.25) {
            return { label: 'Low Stock', color: '#eab308', dotClass: 'yellow' };
        }
        return { label: 'In Stock', color: '#10b981', dotClass: 'green' };
    };

    // const getMinStockDisplay = () => {
    //     const min = item.min_stock_level;
    //     if (min === null || min === undefined) return 'Min. Stock: —';

    //     const convertedMin = convertQuantity(min, item.ingredient_unit, unitOfMeasure);
    //     const formattedMin = new Intl.NumberFormat('en-US', {
    //         maximumFractionDigits: 2
    //     }).format(convertedMin);

    //     let unitLabel = '';
    //     switch (unitOfMeasure) {
    //         case 'MG': unitLabel = 'mg'; break;
    //         case 'GRAMS': unitLabel = 'g'; break;
    //         case 'KG': unitLabel = 'kg'; break;
    //         case 'ML': unitLabel = 'ml'; break;
    //         case 'LITERS': unitLabel = 'L'; break;
    //         case 'S': unitLabel = 'units'; break;
    //         default: unitLabel = unitOfMeasure?.toLowerCase() || '';
    //     }

    //     return `Min. Stock: ${formattedMin} ${unitLabel}`;
    // };

    // Determine which units to show in dropdown
    const isWeightUnit = ['MG', 'GRAMS', 'KG'].includes(item.ingredient_unit);
    const isVolumeUnit = ['ML', 'LITERS'].includes(item.ingredient_unit);
    const isCountableUnit = ['S', 'UNITS'].includes(item.ingredient_unit);

    return (
        <div className="inventory-item">
            <div className="item-name-wrapper">
                <div className="item-name">
                    {item.ingredient_name}
                </div>
                <span className="ingredient-type-badge">
                    {item.is_packaging_material ? 'PKG' : 'Raw'}
                </span>
            </div>

            <div className="item-controls">
                <div className={`quantity-input-container ${error ? 'has-error' : ''}`}>
                    <button
                        className="quantity-button decrement"
                        onClick={handleDecrement}
                        aria-label={`Decrease ${item.ingredient_name} quantity`}
                    >
                        −
                    </button>

                    <input
                        type="text"
                        className="quantity-input"
                        value={quantity}
                        onChange={handleQuantityChange}
                        onBlur={handleQuantityBlur}
                        aria-label={`${item.ingredient_name} quantity in ${unitOfMeasure}`}
                        aria-invalid={!!error}
                        role="spinbutton"
                        aria-valuemin="0"
                        aria-valuemax="999999"
                        aria-valuenow={parseFloat(quantity) || 0}
                    />

                    <button
                        className="quantity-button increment"
                        onClick={handleIncrement}
                        aria-label={`Increase ${item.ingredient_name} quantity`}
                    >
                        +
                    </button>

                    <div className="unit-select-container">
                        <select
                            className="unit-select"
                            value={unitOfMeasure}
                            onChange={handleUnitChange}
                            aria-label={`Unit of measure for ${item.ingredient_name}`}
                        >
                            {isWeightUnit && (
                                <>
                                    <option value="MG">Milligrams (mg)</option>
                                    <option value="GRAMS">Grams (g)</option>
                                    <option value="KG">Kilograms (kg)</option>
                                </>
                            )}

                            {isVolumeUnit && (
                                <>
                                    <option value="ML">Milliliters (ml)</option>
                                    <option value="LITERS">Liters (L)</option>
                                </>
                            )}

                            {isCountableUnit && (
                                <option value="S">Units</option>
                            )}

                            {!isWeightUnit && !isVolumeUnit && !isCountableUnit && (
                                <option value={item.ingredient_unit}>{item.ingredient_unit}</option>
                            )}
                        </select>
                    </div>
                </div>

                {error && <div className="error-message">{error}</div>}
            </div>

            <div className="item-metadata-row">

                <span className="item-status" style={{ color: getStockStatus().color }}>
                    <span className={`status-bullet-dot ${getStockStatus().dotClass}`}>●</span>
                    {getStockStatus().label}
                </span>

                <button
                    type="button"
                    className={`item-details-toggle ${expanded ? 'open' : ''}`}
                    onClick={toggleExpanded}
                    aria-expanded={expanded}
                    aria-label={`${expanded ? 'Hide' : 'Show'} details for ${item.ingredient_name}`}
                >
                    Details
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                        <polyline points="6 9 12 15 18 9" />
                    </svg>
                </button>
            </div>

            {expanded && (
                <div className="item-details-panel">
                    <div className="item-detail-row">
                        <span className="item-detail-label">Supplier</span>
                        <span className="item-detail-value">
                            {item.supplier || '—'}
                            {item.supplier_sku ? ` (SKU: ${item.supplier_sku})` : ''}
                        </span>
                    </div>

                    <div className="item-detail-row">
                        <span className="item-detail-label">Used in</span>
                        <div className="item-detail-value">
                            {productsLoading && 'Loading…'}
                            {productsError && productsError}
                            {!productsLoading && !productsError && productsInfo && (
                                productsInfo.products.length > 0 ? (
                                    <div className="item-products-list">
                                        {productsInfo.products.map(p => (
                                            <div key={p.product_id} className="item-product-row">
                                                <span className="item-product-name">{p.product_name}</span>
                                                <span className="item-product-qty">{formatQuantity(p.quantity_per_keg, item.ingredient_unit)}/keg</span>
                                            </div>
                                        ))}
                                    </div>
                                ) : 'Not used in any product recipe'
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default InventoryItem;